import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';

interface Transaction {
  id: string;
  user_id: string;
  recipient_id?: string;
  sender_id?: string;
  amount: number;
  type: 'topup' | 'withdraw' | 'transfer' | 'receive';
  status: string;
  created_at: string;
  recipient_email?: string;
  other_party_email?: string;
  direction?: 'sent' | 'received';
  is_received?: boolean;
}

export default function Transfer() {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [recentTransfers, setRecentTransfers] = useState<Transaction[]>([]);
  const [recipientExists, setRecipientExists] = useState<boolean | null>(null);
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const user = getCurrentUser();
  const transferCompleted = useRef(false);

  useEffect(() => {
    if (user) {
      loadUserBalance();
      loadRecentTransfers();
    }
  }, [user]);

  // Check if recipient exists when email changes
  useEffect(() => {
    const checkRecipient = async () => {
      if (!recipientEmail || recipientEmail.trim() === '') {
        setRecipientExists(null);
        setRecipientId(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('stripeusers')
          .select('id, email')
          .eq('email', recipientEmail.toLowerCase())
          .single();

        if (error) {
          console.log('Recipient check error:', error);
          setRecipientExists(false);
          setRecipientId(null);
        } else if (data) {
          setRecipientExists(true);
          setRecipientId(data.id);
        }
      } catch (error) {
        console.error('Error checking recipient:', error);
        setRecipientExists(false);
        setRecipientId(null);
      }
    };

    // Debounce the check to avoid too many requests
    const timeoutId = setTimeout(() => {
      if (recipientEmail) {
        checkRecipient();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [recipientEmail]);

  const loadUserBalance = async () => {
    try {
      if (!user) return;
      
      // Get balance from stripeusers
      const { data, error } = await supabase
        .from('stripeusers')
        .select('wallet_balance')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching balance:', error);
        throw error;
      }
      
      if (data) {
        const numericBalance = parseFloat(data.wallet_balance);
        console.log('Loaded balance:', numericBalance);
        setBalance(numericBalance);
        
        // Update local storage with latest balance
        const updatedUser = { ...user, wallet_balance: numericBalance };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error loading balance:', error);
      toast.error('Failed to load wallet balance');
    }
  };

  const loadRecentTransfers = async () => {
    try {
      if (!user) return;
      
      // Get transfers from stripetransactions
      const { data: sentTransfers, error: sentError } = await supabase
        .from('stripetransactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'transfer')
        .order('created_at', { ascending: false })
        .limit(10);

      if (sentError) {
        console.error('Error fetching sent transfers:', sentError);
      }
      
      const { data: receivedTransfers, error: receivedError } = await supabase
        .from('stripetransactions')
        .select('*')
        .eq('recipient_id', user.id)
        .eq('type', 'transfer')
        .order('created_at', { ascending: false })
        .limit(10);

      if (receivedError) {
        console.error('Error fetching received transfers:', receivedError);
      }
      
      // Format transfers
      const formattedSentTransfers = (sentTransfers || []).map(transfer => ({
        ...transfer,
        direction: 'sent'
      }));
      
      const formattedReceivedTransfers = (receivedTransfers || []).map(transfer => ({
        ...transfer,
        direction: 'received'
      }));
      
      // Add email information
      const transfersWithEmails = await Promise.all([
        ...formattedSentTransfers,
        ...formattedReceivedTransfers
      ].map(async (transfer) => {
        try {
          if (transfer.direction === 'sent') {
            // Get recipient email
            const { data: recipient } = await supabase
              .from('stripeusers')
              .select('email')
              .eq('id', transfer.recipient_id)
              .single();
              
            return {
              ...transfer,
              other_party_email: recipient?.email || 'Unknown'
            };
          } else {
            // Get sender email
            const { data: sender } = await supabase
              .from('stripeusers')
              .select('email')
              .eq('id', transfer.user_id)
              .single();
              
            return {
              ...transfer,
              other_party_email: sender?.email || 'Unknown'
            };
          }
        } catch (error) {
          return {
            ...transfer,
            other_party_email: 'Unknown'
          };
        }
      }));
      
      // Sort by date
      const sortedTransfers = transfersWithEmails.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setRecentTransfers(sortedTransfers);
    } catch (error) {
      console.error('Error loading recent transfers:', error);
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (transferAmount > balance) {
      toast.error('Insufficient balance');
      return;
    }

    if (!recipientExists || !recipientId) {
      toast.error('Recipient not found');
      return;
    }

    if (recipientId === user.id) {
      toast.error('You cannot transfer money to yourself');
      return;
    }

    setLoading(true);
    try {
      // 1. Get sender's current balance
      const { data: sender, error: senderError } = await supabase
        .from('stripeusers')
        .select('wallet_balance')
        .eq('id', user.id)
        .single();
        
      if (senderError) {
        throw new Error('Failed to get sender balance');
      }
      
      // 2. Get recipient's balance
      const { data: recipient, error: recipientError } = await supabase
        .from('stripeusers')
        .select('wallet_balance, email')
        .eq('id', recipientId)
        .single();
        
      if (recipientError) {
        throw new Error('Failed to get recipient balance');
      }
      
      // 3. Calculate new balances
      const senderBalance = parseFloat(sender.wallet_balance);
      const recipientBalance = parseFloat(recipient.wallet_balance);
      
      if (senderBalance < transferAmount) {
        throw new Error('Insufficient balance');
      }
      
      const newSenderBalance = senderBalance - transferAmount;
      const newRecipientBalance = recipientBalance + transferAmount;
      
      // 4. Create transaction record
      const { data: transaction, error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          recipient_id: recipientId,
          amount: transferAmount,
          type: 'transfer',
          status: 'pending'
        })
        .select()
        .single();
        
      if (transactionError) {
        throw new Error('Failed to create transaction record');
      }
      
      // 5. Update sender balance
      const { error: updateSenderError } = await supabase
        .from('stripeusers')
        .update({ wallet_balance: newSenderBalance })
        .eq('id', user.id);
        
      if (updateSenderError) {
        // Mark transaction as failed
        await supabase
          .from('stripetransactions')
          .update({ status: 'failed' })
          .eq('id', transaction.id);
          
        throw new Error('Failed to update sender balance');
      }
      
      // 6. Update recipient balance
      const { error: updateRecipientError } = await supabase
        .from('stripeusers')
        .update({ wallet_balance: newRecipientBalance })
        .eq('id', recipientId);
        
      if (updateRecipientError) {
        // Rollback sender balance
        await supabase
          .from('stripeusers')
          .update({ wallet_balance: senderBalance })
          .eq('id', user.id);
          
        // Mark transaction as failed
        await supabase
          .from('stripetransactions')
          .update({ status: 'failed' })
          .eq('id', transaction.id);
          
        throw new Error('Failed to update recipient balance');
      }
      
      // 7. Mark transaction as completed
      await supabase
        .from('stripetransactions')
        .update({ status: 'completed' })
        .eq('id', transaction.id);
      
      // 8. Create a receive transaction record for the recipient
      await supabase
        .from('stripetransactions')
        .insert({
          user_id: recipientId,
          amount: transferAmount,
          type: 'receive',
          status: 'completed',
          sender_id: user.id
        });
      
      // Update local balance
      setBalance(newSenderBalance);
      
      // Update user in localStorage
      const updatedUser = { ...user, wallet_balance: newSenderBalance };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success(`Successfully transferred $${transferAmount} to ${recipientEmail}`);
      
      // Reset form
      setAmount('');
      setRecipientEmail('');
      setRecipientExists(null);
      setRecipientId(null);
      
      // Set flag to indicate transfer completed
      transferCompleted.current = true;
      
      // Reload transfers
      loadRecentTransfers();
    } catch (error: any) {
      toast.error(error.message || 'Transfer failed');
      console.error('Transfer error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to reload data after transfer
  useEffect(() => {
    if (transferCompleted.current) {
      // Reset the flag
      transferCompleted.current = false;
      
      // Add a small delay to ensure database updates have propagated
      const timer = setTimeout(() => {
        loadUserBalance();
        loadRecentTransfers();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [recentTransfers]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString ('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to Dashboard
        </Link>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-black text-white p-6">
            <h1 className="text-2xl font-bold">Transfer Money</h1>
            <p className="mt-2 text-gray-300">
              Send money instantly to other users
            </p>
          </div>

          {/* Balance Display */}
          <div className="bg-indigo-50 p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-indigo-600">Your Current Balance</p>
                <p className="text-2xl font-bold text-gray-900">${balance.toFixed(2)}</p>
              </div>
              <button
                onClick={loadUserBalance}
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
              >
                Refresh Balance
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              How to Transfer Money
            </h2>
            <ol className="list-decimal list-inside space-y-3 text-gray-600">
              <li>Enter the recipient's email address (must be a registered user)</li>
              <li>Enter the amount you want to send</li>
              <li>Review the details and click "Transfer"</li>
              <li>The money will be instantly transferred to the recipient's wallet</li>
            </ol>
          </div>
          
          {/* Transfer Form */}
          <form onSubmit={handleTransfer} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient's Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    recipientExists === false && recipientEmail
                      ? 'border-red-300 focus:ring-red-500'
                      : recipientExists === true
                      ? 'border-green-300 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-black'
                  } focus:ring-2 focus:border-transparent`}
                  placeholder="Enter recipient's email"
                  required
                />
                {recipientEmail && recipientExists !== null && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {recipientExists ? (
                      <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              {recipientExists === false && recipientEmail && (
                <p className="mt-1 text-sm text-red-600">
                  User not found. Please check the email address.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount to Send
              </label>
              <div className="relative">
                <span className="absolute left-4 top-3 text-gray-500">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Important Notes:</h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Transfers are instant and cannot be reversed</li>
                <li>Make sure the recipient's email is correct</li>
                <li>You can only transfer funds from your available balance</li>
                <li>Minimum transfer amount is $0.01</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading || !recipientExists || recipientId === user?.id}
              className="w-full bg-black text-white py-3 px-4 rounded-lg hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? 'Processing Transfer...' : 'Transfer Money'}
            </button>
            
            {recipientId === user?.id && (
              <p className="text-center text-sm text-red-600">
                You cannot transfer money to yourself
              </p>
            )}
          </form>
        </div>

        {/* Recent Transfers */}
        {recentTransfers.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Recent Transfers</h2>
              <button 
                onClick={loadRecentTransfers}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Refresh
              </button>
            </div>
            <div className="divide-y divide-gray-200">
              {recentTransfers.map((transfer: any) => (
                <div key={transfer.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {transfer.direction === 'sent' ? 'To: ' : 'From: '}
                        {transfer.other_party_email || 'Unknown'}
                      </p>
                      <p className="text-sm text-gray-500">{formatDate(transfer.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${
                        transfer.direction === 'sent' ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {transfer.direction === 'sent' ? '-' : '+'}${transfer.amount.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500 capitalize">{transfer.status}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}